import { useCallback, useState } from 'react';
import { GridApi } from 'ag-grid-community';
import { isDataRow } from './captable-utils';

export function useInvestmentRowData<T>(initialRowData: T[], gridApi: GridApi | null) {
  const [rowData, setRowData] = useState<T[]>(initialRowData);
  const [nSelectedRows, setNSelectedRows] = useState<number>(0);

  const onRowSelected = useCallback(() => {
    if (!gridApi) {
      return;
    }
    // Footer rows are unselectable via grid configuration, so no filtering needed
    setNSelectedRows(gridApi.getSelectedNodes().length);
  }, [gridApi]);

  const onAddRow = useCallback(() => {
    gridApi?.applyTransaction({ add: [{} as T], addIndex: 0 });
    setNSelectedRows(0);
  }, [gridApi]);

  const onDeleteRows = useCallback(() => {
    if (!gridApi) {
      return;
    }
    // Footer rows are unselectable via grid configuration, so no filtering needed
    const selectedNodes = gridApi.getSelectedNodes();
    // Create a Set of selected node data references to match against
    const selectedNodeData = new Set(selectedNodes.map((node) => node.data));
    const currentRowData: T[] = [];
    gridApi.forEachNodeAfterFilterAndSort((node) => {
      // Only process data rows (footer rows are already excluded from selection)
      if (node.data && isDataRow(node)) {
        currentRowData.push(node.data);
      }
    });
    // Filter out rows that match selected node data
    const updated = currentRowData.filter((row) => !selectedNodeData.has(row));
    setRowData(updated);
    setNSelectedRows(0);
  }, [gridApi]);

  const resetRowData = useCallback((rows: T[]) => {
    setRowData(rows);
    setNSelectedRows(0);
  }, []);

  return { rowData, nSelectedRows, onRowSelected, onAddRow, onDeleteRows, resetRowData };
}
